import type { CellClassParams, GetContextMenuItemsParams, GridApi, IRowNode, MenuItemDef } from 'ag-grid-community';
import { entries, get, range } from 'lodash-es';
import type { ToastProps } from '../../hooks';
import { ModeEnum } from '../../types';
import type { Leaves } from '../../utils';
import { format, logger } from '../../utils';
import {
  DEFAULT_BULK_EDIT_NUM_ATTEMPTS,
  DEFAULT_BULK_EDIT_NUM_WORKERS,
  DEFAULT_BULK_EDIT_THROTTLE_MS,
  type ColumnDef,
  type GenericFilter,
} from '../BlotterTable';
import { Button, IconButton } from '../Button';
import type { FilterableProperty } from '../Filters';
import { FormControlSizes } from '../Form';
import { IconName } from '../Icons';
import { NotificationVariants } from '../Notification';
import { Tooltip } from '../Tooltip';
import { isNotDivider } from './components/InputsAndDropdownsDrawer/components/dividerUtils';
import { flattenDrawerOptions } from './components/InputsAndDropdownsDrawer/filterAndMapDrawerOptions';
import type { InputsAndDropdownsDrawerOptions } from './components/InputsAndDropdownsDrawer/types';
import type { EntityAdminClass } from './EntityAdminClass';
import type {
  AllowAddEntityConfig,
  AllowAddEntityResolved,
  BaseEntityAdminProps,
  EntityAdminRecord,
  HierarchicalColumnProps,
  RightPinnedButtonProps,
} from './types';

export function resolveAllowAddEntity(config: AllowAddEntityConfig | undefined): AllowAddEntityResolved {
  if (config === undefined) {
    return { main: false, child: false };
  }
  if (typeof config === 'boolean') {
    return { main: config, child: config };
  }
  return {
    main: config.main,
    child: config.child ?? false,
  };
}

/** True if any add is allowed (for path validation, drawer visibility) */
export function hasAnyAddAllowed(config: AllowAddEntityConfig | undefined): boolean {
  const { main, child } = resolveAllowAddEntity(config);
  return main || child;
}

export function getShowJSONContextItem<T extends EntityAdminRecord>({
  params,
  handleClickJson,
}: {
  params: GetContextMenuItemsParams<T>;
  handleClickJson: (item?: T) => void;
}): MenuItemDef<T> {
  return {
    action: () => handleClickJson(params.node?.data),
    name: 'Show JSON',
    icon: `<i class="ag-icon ${IconName.Braces}"/>`,
  };
}

export const getEditColumn = <T extends EntityAdminRecord>({
  handleOnClick,
  isDisabled = () => false,
  isHidden = () => false,
  isDisabledTooltip,
}: RightPinnedButtonProps<EntityAdminClass<T>> & {
  handleOnClick: (entityClass: EntityAdminClass<T>) => void;
}): ColumnDef<T> => ({
  id: 'edit',
  type: 'custom',
  pinned: 'right',
  frozen: true,
  width: 70,
  suppressColumnsToolPanel: true,
  params: {
    cellRenderer: ({ node }: { node: IRowNode<EntityAdminClass<T>> }) => {
      if (node.data == null) {
        return null;
      }
      if (isHidden(node.data)) {
        return null;
      }
      const isNodeDisabled = isDisabled(node.data);
      return (
        <Tooltip tooltip={isNodeDisabled ? isDisabledTooltip : undefined}>
          <Button
            startIcon={IconName.Pencil}
            size={FormControlSizes.Small}
            onClick={() => {
              const entityClass = node.data;
              entityClass && handleOnClick(entityClass);
              node.setSelected(true, true);
            }}
            disabled={isDisabled(node.data)}
          >
            Edit
          </Button>
        </Tooltip>
      );
    },
  },
});
export const getDeleteColumn = <T extends EntityAdminRecord>({
  handleOnClickDelete,
  isDisabled = () => false,
  isHidden = () => false,
  isDisabledTooltip,
}: RightPinnedButtonProps<EntityAdminClass<T>> & {
  handleOnClickDelete: (entityClass: EntityAdminClass<T>) => void;
}): ColumnDef<T> => ({
  id: 'delete',
  type: 'custom',
  pinned: 'right',
  frozen: true,
  width: 42,
  suppressColumnsToolPanel: true,
  params: {
    cellRenderer: ({ node }: { node: IRowNode<EntityAdminClass<T>> }) => {
      if (node.data == null) {
        return null;
      }
      if (isHidden(node.data)) {
        return null;
      }
      const isNodeDisabled = isDisabled(node.data);
      return (
        <Tooltip tooltip={isNodeDisabled ? isDisabledTooltip : undefined}>
          <IconButton
            disabled={isNodeDisabled}
            icon={IconName.Trash}
            size={FormControlSizes.Small}
            onClick={() => {
              const entityClass = node.data;
              entityClass && handleOnClickDelete(entityClass);
              node.setSelected(true, true);
            }}
          />
        </Tooltip>
      );
    },
  },
});
export const getModeColumn = <T extends EntityAdminRecord>({
  handleOnSwitchMode,
  modeField,
  isDisabled = () => false,
  isHidden = () => false,
}: {
  handleOnSwitchMode: (entityClass: EntityAdminClass<T>, newMode: ModeEnum) => void;
  modeField?: NonNullable<Exclude<BaseEntityAdminProps<T>['allowModeSwitch'], boolean>>['modeField'];
  isDisabled?: NonNullable<Exclude<BaseEntityAdminProps<T>['allowModeSwitch'], boolean>>['isDisabled'];
  isHidden?: NonNullable<Exclude<BaseEntityAdminProps<T>['allowModeSwitch'], boolean>>['isHidden'];
}): ColumnDef<T> => ({
  id: 'mode',
  field: modeField,
  type: 'toggle',
  title: 'Mode',
  description: 'Enabled/Disabled',
  pinned: 'left',
  width: 72, // correct width to show "Mode" and not "Mo..."
  params: {
    valueSetter: params => {
      if (params.node == null) {
        return false;
      }
      const entityClass = params.node.data as EntityAdminClass<T>;
      const newMode = params.newValue ? ModeEnum.Enabled : ModeEnum.Disabled;
      entityClass && handleOnSwitchMode(entityClass, newMode);
      // false because in case of confirmation, we don't want to update the value just yet;
      // it'll get updated when the data changes
      return false;
    },
    isDisabled: (data: T | undefined) => data == null || isDisabled?.(data),
    isHidden: (data: T | undefined) => data == null || isHidden?.(data),
  },
});

export const getAddChildEntityColumn = <T extends EntityAdminRecord>({
  openEntityAdminDrawer,
  buttonProps,
}: HierarchicalColumnProps<T>): ColumnDef<T> => ({
  id: 'add-child',
  type: 'button',
  pinned: 'right',
  frozen: true,
  width: buttonProps?.width ?? 100,
  suppressColumnsToolPanel: true,
  params: {
    disabled: ({ node }: { node: IRowNode<EntityAdminClass<T>> }) => {
      if (buttonProps?.disabled) {
        return buttonProps.disabled({ node });
      }
      return node.data?.entityIDField == null;
    },
    startIcon: IconName.Plus,
    onClick: ({ node, api }: { node: IRowNode<EntityAdminClass<T>>; api: GridApi<EntityAdminClass<T>> }) => {
      if (node.data == null) {
        return;
      }
      // Use custom onClick if provided, otherwise use default behavior
      // Note: AgGridButton already calls stopPropagation() to prevent row click handlers from firing
      if (buttonProps?.onClick) {
        buttonProps.onClick({ node, api, openEntityAdminDrawer });
      } else {
        const setupEntity = node.data.clone();
        openEntityAdminDrawer({ setupEntities: [setupEntity], addingChildEntity: true });
      }
      api.deselectAll();
    },
    hide: (data: T) => {
      if (buttonProps?.hide) {
        return buttonProps.hide(data);
      }
      return get(data, data.childIDField) != null;
    },
    children: buttonProps?.text ?? 'Add Child',
    size: FormControlSizes.Small,
  },
});

export const applyInheritanceCellStyle = <T extends EntityAdminRecord>(column: ColumnDef<T>): ColumnDef<T> => ({
  ...column,
  cellStyle: (params: CellClassParams<EntityAdminClass<T>>) => ({
    ...(typeof column.cellStyle === 'function' ? column.cellStyle(params) : column.cellStyle),
    // If this column field is inherited, make it slightly transparent. We overwrite any existing cellStyle opacity.
    opacity: params.data?.isFieldInherited(params.colDef.field as keyof T) ? 0.5 : 1,
  }),
});

export const onlyServerFilterEntries = (
  filter: GenericFilter,
  filterableProperties: FilterableProperty[]
): GenericFilter => {
  return entries(filter).reduce((acc, [key, values]) => {
    const foundProperty = filterableProperties.find(property => property.key === key);
    // EntityAdmin currently only supports server filters that are single-select (see the .at(0) below)
    // This happens to work because the only server filter property used in EA is Counterparties, which is single-select
    // Enhancement + tests needed here
    if (foundProperty && values?.at(0)) {
      acc[foundProperty.field!] = values.at(0)!;
    }
    return acc;
  }, {} as GenericFilter);
};

export async function delay(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Process a set of entities in parallel with retry‐on‐fail logic.
 */
export async function bulkEditQueue<T>(
  entities: T[],
  newEntity: EntityAdminRecord,
  updateFn: (
    entity: T,
    newEntity: EntityAdminRecord,
    showSuccess?: boolean,
    showError?: boolean
  ) => Promise<void | EntityAdminRecord | undefined>,
  addToast: (props: ToastProps) => void,
  removeToast: (id: string) => void
): Promise<void> {
  const total = entities.length;
  const toastID = 'bulk-edit-toast';

  addToast({
    id: toastID,
    text: `Bulk editing ${format(total)} entities…`,
    variant: NotificationVariants.Attention,
    timeout: null,
    dismissable: false,
  });

  let success = 0;
  let failure = 0;
  const queue = entities.slice();

  async function attemptUpdate(entity: T): Promise<boolean> {
    const maxAttempts = DEFAULT_BULK_EDIT_NUM_ATTEMPTS;
    let attempt = 1;

    while (attempt <= maxAttempts) {
      try {
        // never display success here, and only display an error from the final attempt.
        await updateFn(entity, newEntity, false, attempt === maxAttempts);
        return true;
      } catch {
        if (attempt === maxAttempts) {
          logger.warn(`Could not update entity after ${attempt} attempts.`, { entity });
          return false;
        }
        // throttle before next retry
        await delay(DEFAULT_BULK_EDIT_THROTTLE_MS * attempt);
        attempt++;
      }
    }

    return false;
  }

  async function worker() {
    while (queue.length) {
      const item = queue.shift()!;
      const ok = await attemptUpdate(item);
      ok ? success++ : failure++;

      removeToast(toastID);
      addToast({
        id: toastID,
        text: `Applied ${format(success)}/${format(total)}…`,
        variant: NotificationVariants.Attention,
        timeout: null,
        dismissable: false,
      });
    }
  }

  await Promise.all(range(DEFAULT_BULK_EDIT_NUM_WORKERS).map(() => worker()));

  removeToast(toastID);
  if (success === total) {
    addToast({ text: `All changes applied to ${format(success)} entities.`, variant: NotificationVariants.Positive });
  } else if (success > 0) {
    addToast({
      text: `Partially applied changes: ${format(success)} succeeded and ${format(failure)} failed.`,
      variant: NotificationVariants.Warning,
    });
  } else {
    addToast({
      text: `No changes were applied to ${format(failure)} entities.`,
      variant: NotificationVariants.Negative,
    });
  }
}

/**
 * Extracts field names from drawer options, filtering out divider options that don't have fields.
 */
export function extractFieldsFromDrawerOptions<TDrawerRecord extends EntityAdminRecord>(
  drawerOptions: InputsAndDropdownsDrawerOptions<TDrawerRecord>
): (keyof TDrawerRecord)[] {
  return flattenDrawerOptions(drawerOptions)
    .filter(isNotDivider)
    .map(option => option.field);
}

export function removeHiddenFields<TDrawerRecord extends EntityAdminRecord>(
  entity: TDrawerRecord,
  drawerOptions: InputsAndDropdownsDrawerOptions<TDrawerRecord>
): TDrawerRecord {
  // Remove all Drawer Hidden fields from the entity to PATCH/POST.
  const entityCopy = { ...entity };
  flattenDrawerOptions(drawerOptions)
    .filter(isNotDivider)
    // This specific case for entityCopy === originalEntity is ok because we haven't stripped values from the entity yet
    // and some isHidden handlers look at the original entity for full context
    .filter(option => option.getIsHidden?.(entityCopy, entityCopy))
    .forEach(({ field }) => {
      delete entityCopy[field];
    });

  return entityCopy;
}

/**
 * Determines the parent entity from selected entities when adding a child entity.
 */
function getParentEntity<TRecord extends EntityAdminRecord>(
  addingChildEntity: boolean,
  selectedEntities: EntityAdminClass<TRecord>[]
): TRecord | undefined {
  const firstSelectedEntity = selectedEntities.at(0);
  if (!firstSelectedEntity) {
    return undefined;
  }
  return addingChildEntity ? firstSelectedEntity.data : firstSelectedEntity.parentEntity;
}

/**
 * Creates the save context object for EntityAdmin drawer save operations.
 */
export function createSaveContext<TRecord extends EntityAdminRecord>(params: {
  isEditing: boolean;
  addingChildEntity: boolean;
  selectedEntities: EntityAdminClass<TRecord>[];
}): { isCreate: boolean; parentEntity?: TRecord } {
  const { isEditing, addingChildEntity, selectedEntities } = params;
  return {
    isCreate: !isEditing,
    parentEntity: getParentEntity(addingChildEntity, selectedEntities),
  };
}

/**
 * Constructs the row ID for an entity, matching the logic in EntityAdminClass[ENTITY_INTERNAL_ROW_ID].
 * For parent rows: `${entityID}`. For child rows: `${entityID}-${childID}`.
 */
export function getEntityRowId<TRecord extends EntityAdminRecord>(
  entity: TRecord,
  entityIDField: Leaves<TRecord>,
  childIDField?: Leaves<TRecord>
): string {
  const entityID = get(entity, entityIDField);
  const childEntityID = childIDField ? get(entity, childIDField) : undefined;
  return childEntityID ? `${entityID}-${childEntityID}` : `${entityID}`;
}
